import { useState, useEffect, useMemo, useCallback } from 'react';
import { Notification, NotificationFormData, NotificationCondition } from '../types';
import { useNotificationsSettings, useNotificationEvents, useNotificationFields } from './useNotificationsSettings';

interface UseNotificationFormParams {
    notification: Notification | null;
    onClose: () => void;
    onSave: (data: NotificationFormData) => Promise<void>;
}

export const useNotificationForm = ({ notification, onClose, onSave }: UseNotificationFormParams) => {
    const { recordTypes, creating, updating, allNotifications } = useNotificationsSettings();
    const [formData, setFormData] = useState<NotificationFormData>({
        name: '',
        recordTypeId: '',
        eventId: '',
        applyCondition: false,
        priority: 'regular',
        frequency: 'real_time'
    });

    const [showCondition, setShowCondition] = useState(false);

    const { events, loading: loadingEvents } = useNotificationEvents(formData.recordTypeId || null);
    const { fields, loading: loadingFields } = useNotificationFields(formData.recordTypeId || null);

    const hasValidFields = fields.filter((field) => Boolean(Number(field.id))).length > 0;
    const canApplyCondition = formData.recordTypeId && hasValidFields && !loadingFields;

    useEffect(() => {
        if (notification) {
            const hasCondition = !!notification.condition;
            setFormData({
                name: notification.name,
                recordTypeId: notification.recordType.id,
                eventId: notification.event.id,
                applyCondition: hasCondition,
                condition: notification.condition,
                priority: notification.priority,
                frequency: notification.frequency
            });
            setShowCondition(hasCondition);
        }
    }, [notification]);

    const handleConditionToggle = (checked: boolean) => {
        setShowCondition(checked);
        setFormData({
            ...formData,
            applyCondition: checked,
            condition: checked ? formData.condition : undefined
        });
    };

    const handleConditionChange = (condition: NotificationCondition | undefined) => {
        setFormData({
            ...formData,
            condition
        });
    };

    const handleFormChange = (field: keyof NotificationFormData, value: any) => {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    const handleRecordTypeChange = (recordTypeId: string) => {
        setFormData({
            ...formData,
            recordTypeId,
            eventId: ''
        });
    };

    const handleSubmit = async () => {
        try {
            const recordType = recordTypes.find((rt) => rt.id === formData.recordTypeId);
            const event = events.find((e) => e.id === formData.eventId);

            const dataToSave = {
                ...formData,
                recordType,
                event
            };

            await onSave(dataToSave as any);
            onClose();
        } catch (error: any) {
            console.error(error.message);
        }
    };

    const isLoading = creating || updating;

    const getBreadcrumbTitle = () => {
        const prefix = notification ? 'Edit:' : 'New:';

        // if name is not empty, return it
        if (formData.name && formData.name.trim()) {
            return [prefix, formData.name];
        }

        // if name is empty, build the title with the details
        const parts: string[] = [];

        if (formData.recordTypeId) {
            const recordType = recordTypes.find((rt) => rt.id === formData.recordTypeId);
            if (recordType) {
                parts.push(recordType.displayName);
            }
        }

        if (formData.eventId) {
            const event = events.find((e) => e.id === formData.eventId);
            if (event) {
                parts.push(event.displayName);
            }
        }

        if (formData.condition?.fieldName) {
            parts.push(formData.condition.fieldName);
        }

        return [prefix, parts.length > 0 ? parts.join(', ') : ''];
    };

    const checkIfConfigurationExists = useCallback(() => {
        if (!formData.recordTypeId || !formData.eventId || !allNotifications) return false;

        const duplicates = allNotifications.filter((n) => {
            if (notification && n.id === notification.id) return false;

            if (n.recordType.id !== formData.recordTypeId) return false;
            if (n.event.id !== formData.eventId) return false;

            const hasCondition = !!n.condition;
            const formHasCondition = formData.applyCondition && !!formData.condition;

            if (hasCondition !== formHasCondition) return false;

            if (hasCondition && formHasCondition) {
                const existingCondition = n.condition!;
                const newCondition = formData.condition!;

                if (existingCondition.fieldId !== newCondition.fieldId) return false;
                if (existingCondition.operator !== newCondition.operator) return false;

                const existingValue = String(existingCondition.value || '').toLowerCase();
                const newValue = String(newCondition.value || '').toLowerCase();
                if (existingValue !== newValue) return false;
            }

            return true;
        });

        return duplicates.length > 0;
    }, [formData.recordTypeId, formData.eventId, formData.applyCondition, formData.condition, allNotifications, notification]);

    const isDuplicate = useMemo(() => checkIfConfigurationExists(), [checkIfConfigurationExists]);

    const isFormValid = !!(formData.recordTypeId && formData.eventId && !isDuplicate);

    return {
        formData,
        showCondition,
        recordTypes,
        events,
        fields,
        loadingEvents,
        loadingFields,
        canApplyCondition,
        isLoading,
        isFormValid,
        isDuplicate,
        notification,
        getBreadcrumbTitle,
        handleConditionToggle,
        handleConditionChange,
        handleFormChange,
        handleRecordTypeChange,
        handleSubmit
    };
};
